import {
  mapApiResourceToTokens,
  type AuthenticationTokens,
  type TokenPairApiResource,
} from '@/api/auth/authApi.types';
import {
  mapApiResourceToUser,
  type PlatformUser,
  type UserApiResource,
} from '@/api/users/usersApi.types';

export interface InvitationApiResource {
  id: string;
  email: string;
  full_name: string | null;
  role_id: string;
  scope_code: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface Invitation {
  id: string;
  email: string;
  fullName: string | null;
  roleId: string;
  scopeCode: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationPreviewApiResource {
  email: string;
  full_name: string | null;
  phone: string | null;
  role_name: string;
  scope_code: string;
  is_owner: boolean;
  account_exists: boolean;
  workspace_name: string | null;
  language: string;
}

export interface InvitationPreview {
  email: string;
  fullName: string | null;
  phone: string | null;
  roleName: string;
  scopeCode: string;
  isOwner: boolean;
  accountExists: boolean;
  workspaceName: string | null;
  language: string;
}

export interface AcceptedMembershipApiResource {
  tenant_id: string | null;
  scope_code: string;
}

export interface AcceptedMembership {
  tenantId: string | null;
  scopeCode: string;
}

export interface InvitationWithLinkApiResource {
  invitation: InvitationApiResource;
  invite_link: string;
}

export interface InvitationWithLink {
  invitation: Invitation;
  inviteLink: string;
}

export interface CreateInvitationPayload {
  email: string;
  fullName: string | null;
  roleId: string;
  scopeCode: string;
}

export interface AcceptInvitationPayload {
  token: string;
  fullName: string;
  phone: string | null;
  password: string;
  preferredLanguage: string;
  acceptTerms: boolean;
}

export interface AcceptedInvitationApiResource {
  user: UserApiResource;
  tokens: TokenPairApiResource;
}

export interface AcceptedInvitation {
  user: PlatformUser;
  tokens: AuthenticationTokens;
}

export function mapApiResourceToInvitation(resource: InvitationApiResource): Invitation {
  return {
    id: resource.id,
    email: resource.email,
    fullName: resource.full_name,
    roleId: resource.role_id,
    scopeCode: resource.scope_code,
    status: resource.status,
    expiresAt: resource.expires_at,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToInvitationWithLink(
  resource: InvitationWithLinkApiResource
): InvitationWithLink {
  return {
    invitation: mapApiResourceToInvitation(resource.invitation),
    inviteLink: resource.invite_link,
  };
}

export function mapApiResourceToInvitationPreview(
  resource: InvitationPreviewApiResource
): InvitationPreview {
  return {
    email: resource.email,
    fullName: resource.full_name,
    phone: resource.phone,
    roleName: resource.role_name,
    scopeCode: resource.scope_code,
    isOwner: resource.is_owner,
    accountExists: resource.account_exists,
    workspaceName: resource.workspace_name,
    language: resource.language,
  };
}

export function mapApiResourceToAcceptedMembership(
  resource: AcceptedMembershipApiResource
): AcceptedMembership {
  return {
    tenantId: resource.tenant_id,
    scopeCode: resource.scope_code,
  };
}

export function mapApiResourceToAcceptedInvitation(
  resource: AcceptedInvitationApiResource
): AcceptedInvitation {
  return {
    user: mapApiResourceToUser(resource.user),
    tokens: mapApiResourceToTokens(resource.tokens),
  };
}
